import React, { useEffect, useState } from 'react';
import {
    MdClose,
    MdExitToApp,
    MdKeyboardArrowUp,
    MdKeyboardArrowDown,
    MdOutlineBookmark,
    MdOutlineBookmarkBorder,
    MdOutlineKeyboard,
    MdOutlineLocationOn,
    MdOutlineTimer,
    MdWarning,
    MdOutlineTimerOff 
} from 'react-icons/md';
import moment from 'moment';
import Button from 'components/Button';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import usePrompt from 'hooks/usePrompt';
import { useAppSelector } from 'hooks/useStore';
import { useOptionalTTSContext } from 'contexts/TTSContext';
import { MULTIPLE_CHOICES, DIGITAL_TIMER_WARNING_TIME } from 'global/constants';
import { getDeviceOS } from 'global/os';
import { latexToSpeech } from 'global/latexToSpeech';
import Shortcuts from './Shortcuts';
import FigureViewer from '../FigureViewer';
import ToolErrorBoundary from '../ToolErrorBoundary';
import './index.css';

import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";
import { flattenKatexUnderlines } from 'global/katexWrap';

type DigitalModuleProps = {
    moduleName: string;
    subject?: string;
    timeLeft?: number;
    isUntimed?: boolean;
    prompt?: string;
    ttsEnabled?: boolean;
    questions: Array<{
        question: string;
        id: string;
        choices: Array<{
            id: string;
            choice?: string;
            figure?: string;
            figureTtsDescription?: string;
            isCorrect?: boolean;
        }>;
        passages: Array<{
            id: string;
            introduction?: string;
            passage: string;
            order?: number;
        }>;
        figures: Array<{
            id: string;
            image: string;
            ttsDescription?: string;
            order?: number;
        }>;
    }>;
    questionIndex: number;
    answers: Array<{
        questionId: string;
        answer?: string;
        marked?: boolean;
        crossedOut?: string[];
        timeSpent?: number;
    }>;
    markQuestion?: () => void;
    crossOutChoice?: (choiceId: string) => void;
    handleChange: (event: any) => void;
    handleSelect: (event: any) => void;
    handleSaveAndExit: () => void;
    handleDiscardAndExit: () => void;
    handleSubmit: () => void;
    navigateQuestion: (index: number) => void;
};

function DigitalBCAModule({
    moduleName,
    subject,
    timeLeft,
    isUntimed,
    prompt,
    questions,
    questionIndex,
    answers,
    ttsEnabled,
    markQuestion,
    crossOutChoice,
    handleChange,
    handleSelect,
    handleSaveAndExit,
    handleDiscardAndExit,
    handleSubmit,
    navigateQuestion
}: DigitalModuleProps) {
    const [hideTimer, setHideTimer] = useState(false);
    const [directionsOpen, setDirectionsOpen] = useState(true);
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [crossOut, setCrossOut] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [exitOpen, setExitOpen] = useState(false);
    const [shortcutKeyPressed, setShortcutKeyPressed] = useState(false);
    const { user } = useAppSelector(state => state.user);
    const tts = useOptionalTTSContext();
    const ttsActiveId = tts?.activeBlockId || null;
    const ttsEnabledFlag = tts?.enabled ?? false;
    const registerBlock = tts?.registerBlock;
    const directionsRef = useOutsideClickHandler(() => setTimeout(() => setDirectionsOpen(false), 10));
    const navigatorRef = useOutsideClickHandler(() => setTimeout(() => setNavigatorOpen(false), 10));

    usePrompt(
        'Your work may be lost. Are you sure you want to exit?',
        timeLeft > 0
    );

    const preventNavigation = (event: BeforeUnloadEvent) => {
        if (timeLeft > 0) {
            event.preventDefault();
            event.returnValue = '';
        }
    }

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const goToQuestion = (index: number) => {
        navigateQuestion(index);

        let question = document.getElementById('bcaModule__questionChoices');

        if (question) question.scrollTop = 0;
    }

    const handleWindowsShortcutKeys = (event: KeyboardEvent) => {
        const {
            ctrlKey,
            altKey,
            shiftKey,
            code
        } = event;

        if (ctrlKey && altKey && shiftKey) {
            switch(code) {
                case 'KeyD':
                    setDirectionsOpen(!directionsOpen);
                    break;
                default:
                    return;
            }
        }
        else if (ctrlKey && altKey) {
            switch(code) {
                case 'KeyB':
                    if (questionIndex > 0) {
                        goToQuestion(questionIndex - 1);
                    }
                    break;
                case 'KeyX':
                    if (questionIndex < questions.length) {
                        goToQuestion(questionIndex + 1);
                    }
                    // else {
                    //     handleSubmit();
                    // }
                    break;
                case 'KeyT':
                    setHideTimer(!hideTimer);
                    break;
                case 'KeyV':
                    markQuestion();
                    break;
                case 'KeyM':
                    if (questions[questionIndex].choices.length > 0) {
                        setCrossOut(!crossOut);
                    }
                    break;
                case 'Digit1':
                    if (questions[questionIndex].choices.length > 0) {
                        crossOutChoice(questions[questionIndex].choices[0].id);
                    }
                    break;
                case 'Digit2':
                    if (questions[questionIndex].choices.length > 0) {
                        crossOutChoice(questions[questionIndex].choices[1].id);
                    }
                    break;
                case 'Digit3':
                    if (questions[questionIndex].choices.length > 0) {
                        crossOutChoice(questions[questionIndex].choices[2].id);
                    }
                    break;
                case 'Digit4':
                    if (questions[questionIndex].choices.length > 0) {
                        crossOutChoice(questions[questionIndex].choices[3].id);
                    }
                    break;
                default:
                    return;
            }
            
        }
        else if (ctrlKey && shiftKey) {
            switch(code) {
                case 'Digit1':
                    if (questions[questionIndex].choices.length > 0) {
                        handleSelect(questions[questionIndex].choices[0].id);
                    }
                    break;
                case 'Digit2':
                    if (questions[questionIndex].choices.length > 0) {
                        handleSelect(questions[questionIndex].choices[1].id);
                    }
                    break;
                case 'Digit3':
                    if (questions[questionIndex].choices.length > 0) {
                        handleSelect(questions[questionIndex].choices[2].id);
                    }
                    break;
                case 'Digit4':
                    if (questions[questionIndex].choices.length > 0) {
                        handleSelect(questions[questionIndex].choices[3].id);
                    }
                    break;
                default:
                    return;
            }
        }

        setShortcutKeyPressed(!shortcutKeyPressed);
    }

    const handleMacShortcutKeys = (event: KeyboardEvent) => {
        const {
            ctrlKey,
            altKey,
            shiftKey,
            metaKey,
            code
        } = event;

        if (metaKey && ctrlKey && shiftKey) {
            switch(code) {
                case 'KeyD':
                    setDirectionsOpen(!directionsOpen);
                    break;
                default:
                    return;
            }
        }
        else if (metaKey && ctrlKey) {
            switch(code) {
                case 'KeyB':
                    if (questionIndex > 0) {
                        goToQuestion(questionIndex - 1);
                    }
                    break;
                case 'KeyX':
                    if (questionIndex < questions.length) {
                        goToQuestion(questionIndex + 1);
                    }
                    // else {
                    //     handleSubmit();
                    // }
                    break;
                case 'Digit3':
                    if (questions[questionIndex].choices.length > 0) {
                        handleSelect(questions[questionIndex].choices[0].id);
                    }
                    break;
                case 'Digit4':
                    if (questions[questionIndex].choices.length > 0) {
                        handleSelect(questions[questionIndex].choices[1].id);
                    }
                    break;
                case 'Digit5':
                    if (questions[questionIndex].choices.length > 0) {
                        handleSelect(questions[questionIndex].choices[2].id);
                    }
                    break;
                case 'Digit6':
                    if (questions[questionIndex].choices.length > 0) {
                        handleSelect(questions[questionIndex].choices[3].id);
                    }
                    break;
                case 'Digit7':
                    if (questions[questionIndex].choices.length > 0) {
                        crossOutChoice(questions[questionIndex].choices[0].id);
                    }
                    break;
                case 'Digit8':
                    if (questions[questionIndex].choices.length > 0) {
                        crossOutChoice(questions[questionIndex].choices[1].id);
                    }
                    break;
                case 'Digit9':
                    if (questions[questionIndex].choices.length > 0) {
                        crossOutChoice(questions[questionIndex].choices[2].id);
                    }
                    break;
                case 'Digit0':
                    if (questions[questionIndex].choices.length > 0) {
                        crossOutChoice(questions[questionIndex].choices[3].id);
                    }
                    break;
                default:
                    return;
            }
        }
        else if (metaKey && altKey) {
            switch(code) {
                case 'KeyG':
                    setHideTimer(!hideTimer);
                    break;
                case 'KeyM':
                    if (questions[questionIndex].choices.length > 0) {
                        setCrossOut(!crossOut);
                    }
                    break;
                default:
                    return;
            }
        }
        else if (metaKey && shiftKey) {
            switch(code) {
                case 'KeyV':
                    markQuestion();
                    break;
                default:
                    return;
            }
        }

        setShortcutKeyPressed(!shortcutKeyPressed);
    }

    const dragMove = (element: HTMLElement, header: HTMLElement) => {
        header.style.cursor = 'grab';
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;
    
        const dragMouseDown = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousedown') {
                pos3 = event.clientX;
                pos4 = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = closeDragElement;
            } else if (event.type === 'touchstart') {
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = closeDragElement;
            }
        };
    
        const dragElement = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousemove') {
                pos1 = pos3 - event.clientX;
                pos2 = pos4 - event.clientY;
                pos3 = event.clientX;
                pos4 = event.clientY;

            } else if (event.type === 'touchmove') {
                pos1 = pos3 - event.touches[0].clientX;
                pos2 = pos4 - event.touches[0].clientY;
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
            }

            if (pos3 > 0 && pos4 > 0 && pos3 < window.innerWidth && pos4 < window.innerHeight) {
                element.style.top = (element.offsetTop - pos2) + 'px';
                element.style.left = (element.offsetLeft - pos1) + 'px';
            }
            else {
                document.onmouseup(event);
                closeDragElement();
            }
        };
    
        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        };
    
        header.onmousedown = (event) => {
            if (event.target instanceof Element && (event.target.closest('.bcaModule__popupHeaderClose') || event.target.closest('.bcaModule__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    
        header.ontouchstart = (event) => {
            if (event.target instanceof Element && (event.target.closest('.bcaModule__popupHeaderClose') || event.target.closest('.bcaModule__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    };

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }   

        const wrappingTexts = document.querySelectorAll<HTMLElement>('.katex-html .base');
        for (let i = 0; i < wrappingTexts.length; i++) {
            if (subject === 'Reading and Writing') {
                wrappingTexts[i].classList.add('wrap');
            }
            else {
                wrappingTexts[i].classList.remove('wrap');
            }
        }

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    useEffect(() => {
        window.addEventListener('beforeunload', preventNavigation);
        document.addEventListener('contextmenu', event => event.preventDefault());
        
        setShortcutKeyPressed(true);

        const shortcutsDragEl = document.getElementById('shortcutsDrag');
        const shortcutsHeaderEl = document.getElementById('shortcutsHeader');
        if (shortcutsDragEl && shortcutsHeaderEl) {
            dragMove(shortcutsDragEl, shortcutsHeaderEl);
        }

        return () => {
            window.removeEventListener('beforeunload', preventNavigation);
        }
    }, []);

    useEffect(() => {
        if (getDeviceOS() === 'windows') {
            document.addEventListener('keydown', handleWindowsShortcutKeys);
        }
        else if (getDeviceOS() === 'mac_os') {
            document.addEventListener('keydown', handleMacShortcutKeys);
        }

        return () => {
            if (getDeviceOS() === 'windows') {
                document.removeEventListener('keydown', handleWindowsShortcutKeys);
            }
            else if (getDeviceOS() === 'mac_os') {
                document.removeEventListener('keydown', handleMacShortcutKeys);
            }
        }
    }, [shortcutKeyPressed]);

    useEffect(() => {
        renderKatex();
    }, [subject, questionIndex, answers, directionsOpen]);

    useEffect(() => {
        if (!ttsEnabledFlag || !registerBlock) return;
        const currentQuestion = questions[questionIndex];
        if (!currentQuestion) return;

        const unregisters: Array<() => void> = [];
        let order = 0;

        for (const f of (currentQuestion.figures || [])) {
            if (f?.ttsDescription) {
                unregisters.push(registerBlock({
                    id: `figure-${f.id}`,
                    order: order++,
                    getText: () => latexToSpeech(f.ttsDescription)
                }));
            }
        }

        for (const p of (currentQuestion.passages || [])) {
            if (p?.passage) {
                unregisters.push(registerBlock({
                    id: `passage-${p.id}`,
                    order: order++,
                    getText: () => latexToSpeech(p.passage)
                }));
            }
        }

        if (currentQuestion.question) {
            unregisters.push(registerBlock({
                id: `stem-${currentQuestion.id}`,
                order: order++,
                getText: () => latexToSpeech(currentQuestion.question)
            }));
        }

        for (const [i, c] of (currentQuestion.choices || []).entries()) {
            const letter = MULTIPLE_CHOICES[i] || '';
            if (c?.choice) {
                unregisters.push(registerBlock({
                    id: `choice-${c.id}`,
                    order: order++,
                    getText: () => `Choice ${letter}. ${latexToSpeech(c.choice)}`
                }));
            } else if (c?.figureTtsDescription) {
                unregisters.push(registerBlock({
                    id: `choice-${c.id}`,
                    order: order++,
                    getText: () => `Choice ${letter}. ${latexToSpeech(c.figureTtsDescription)}`
                }));
            }
        }

        return () => { for (const u of unregisters) u(); };
    }, [ttsEnabledFlag, registerBlock, questions, questionIndex]);

    return (
        <div className='bcaModule bcaModule--sat'>
            <div className='bcaModule__header'>
                <div className='bcaModule__titleGroup'>
                    <h1 className='bcaModule__title'>
                        {moduleName}
                    </h1>
                    <button className='bcaModule__directionsButton' onClick={() => setDirectionsOpen(!directionsOpen)}>
                        {directionsOpen
                            ? <u>Directions</u>
                            : <p>Directions</p>
                        }
                        {directionsOpen
                            ? <MdKeyboardArrowUp className='bcaModule__directionsIcon' />
                            : <MdKeyboardArrowDown className='bcaModule__directionsIcon' />
                        }
                    </button>
                </div>
                <div className='bcaModule__timerGroup'>
                    {isUntimed
                    ? <>
                        <MdOutlineTimerOff className='bcaModule__timer'/>
                        <p className='bcaModule__untimedTimer'>Untimed</p>
                    </>
                    : hideTimer
                        ?   <MdOutlineTimer className={timeLeft <= DIGITAL_TIMER_WARNING_TIME
                                ? 'bcaModule__timer--warning'
                                : 'bcaModule__timer'
                            } />
                        :   <p className={timeLeft <= DIGITAL_TIMER_WARNING_TIME
                                ? 'bcaModule__timer--warning'
                                : 'bcaModule__timer'
                            }>
                                {formatTime(timeLeft)}
                            </p>
                    }
                    {!isUntimed &&
                        <button className='bcaModule__hideTimer' onClick={() => setHideTimer(!hideTimer)}>
                            {hideTimer ? 'Show' : 'Hide'}
                        </button>
                    }
                </div>
                <div className='bcaModule__actionGroup'>
                    <button
                        className={shortcutsOpen
                            ? 'bcaModule__action--active'
                            : 'bcaModule__action'
                        }
                        onClick={() => {
                            if (exitOpen) setExitOpen(false);
                            setShortcutsOpen(!shortcutsOpen);
                        }}
                    >
                        <MdOutlineKeyboard className='bcaModule__actionIcon' />
                        <p className='bcaModule__actionText'>Shortcuts</p>
                    </button>
                    <button className='bcaModule__action' onClick={() => {
                        if (shortcutsOpen) setShortcutsOpen(false);
                        setExitOpen(!exitOpen);
                    }}>
                        <MdExitToApp className='bcaModule__actionIcon' />
                        <p className='bcaModule__actionText'>Exit</p>
                    </button>
                </div>
                {directionsOpen &&
                    <div className='bcaModule__directions' ref={directionsRef}>
                        <div className='bcaModule__directionsBubble'>
                            <p className='katex bcaModule__directionsText'>
                                {prompt
                                    ? prompt
                                    : 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis sollicitudin, mi congue condimentum dignissim, ipsum diam tristique velit, et rutrum orci massa non purus. Etiam vitae ante sed velit blandit condimentum. Donec lectus ipsum, volutpat vel tortor a, blandit elementum felis. Mauris a ultricies quam, at molestie ipsum. Cras nec ante justo. Suspendisse egestas lectus sit amet sem maximus auctor. In ut dignissim lorem, ac tristique eros. Nullam eget sollicitudin quam. Quisque pellentesque cursus justo sed ornare. Integer finibus, tortor at facilisis molestie, ligula augue cursus ipsum, eget auctor enim sem a eros. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Pellentesque suscipit, nunc eget mollis consequat, augue odio suscipit enim, vel varius eros leo sit amet magna. Nunc suscipit leo et metus bibendum, a finibus libero finibus. In lacinia scelerisque urna ut pretium. In at est aliquam, dictum odio non, fringilla tellus. Vivamus commodo molestie urna at dapibus. Nulla facilisi. Maecenas sit amet lorem tincidunt, maximus nunc eu, malesuada velit. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Nulla facilisi.'
                                }
                            </p>
                            <Button
                                className='bcaModule__directionsClose'
                                onClick={() => setDirectionsOpen(false)}
                                secondary
                            >
                                CLOSE
                            </Button>
                        </div>
                    </div>
                }
                <div id='shortcutsDrag' className={shortcutsOpen
                    ? 'bcaModule__popup--shortcuts'
                    : 'bcaModule__popup--hidden'
                }>
                    <div id='shortcutsHeader' className='bcaModule__popupHeader'>
                        <h3 className='bcaModule__popupHeaderText'>
                            Keyboard shortcuts{getDeviceOS() === 'windows'
                                ? ': Windows'
                                : getDeviceOS() === 'mac_os'
                                ? ': MacOS'
                                : ''
                            }
                        </h3>
                        <MdClose className='bcaModule__popupHeaderClose' onClick={() => setShortcutsOpen(false)} />
                    </div>
                    <Shortcuts />
                </div>
                <div className={exitOpen
                    ? 'bcaModule__popup--exit'
                    : 'bcaModule__popup--hidden'
                }>
                    <div className='bcaModule__popupHeader'>
                        <h3 className='bcaModule__popupHeaderText'>
                            <MdWarning className='bcaModule__popupHeaderIcon' />
                            Exiting test
                        </h3>
                        <MdClose className='bcaModule__popupHeaderClose' onClick={() => setExitOpen(false)} />
                    </div>
                    <div className='bcaModule__popupBody'>
                        <p className='bcaModule__popupBodyText'>
                            You are about to exit this test. You can save or discard the progress upon exiting.
                        </p>
                        <div className='bcaModule__popupButtonGroup'>
                            <Button
                                className='bcaModule__popupButton'
                                secondary
                                onClick={handleDiscardAndExit}
                            >
                                DISCARD ANSWERS
                            </Button>
                            <Button
                                className='bcaModule__popupButton'
                                onClick={handleSaveAndExit}    
                            >
                                SAVE AND EXIT
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            <div className='bcaModule__dottedLine'></div>
            {questionIndex < questions.length
            ?   <div className='bcaModule__test'>
                    <div
                        className='bcaModule__question'
                        id='bcaModule__question'
                    >
                        <div className='bcaModule__questionContent'>
                            <div className='bcaModule__questionHeader'>
                                <p className='bcaModule__questionNumber'>
                                    {questionIndex + 1}
                                </p>
                                <button className='bcaModule__questionMarkButton' onClick={markQuestion}>
                                    {answers[questionIndex].marked
                                        ? <MdOutlineBookmark className='bcaModule__questionMarkIcon--marked' />
                                        : <MdOutlineBookmarkBorder className='bcaModule__questionMarkIcon' />
                                    }
                                    Mark for review
                                </button>
                                {questions[questionIndex].choices.length > 0 &&
                                    <button
                                        className={crossOut
                                            ? 'bcaModule__crossOutButton--active'
                                            : 'bcaModule__crossOutButton'
                                        }
                                        onClick={() => setCrossOut(!crossOut)}
                                    >
                                        Abc
                                        <div className='bcaModule__crossOutHelper'>
                                            <div className='bcaModule__crossOutHelperBubble'>
                                                <p>Cross out answer choices you think are wrong.</p>
                                            </div>
                                        </div>
                                    </button>
                                }
                            </div>
                            <div className='bcaModule__questionChoices' id='bcaModule__questionChoices'>
                                {questions[questionIndex].figures.map(figure => (
                                    <ToolErrorBoundary
                                        key={figure.id}
                                        fallback={
                                            <img
                                                className={`bcaModule__figure${ttsActiveId === `figure-${figure.id}` ? ' bcaModule__tts--active' : ''}`}
                                                src={figure.image}
                                                alt={figure.ttsDescription || (questionIndex + 1).toString()}
                                                draggable={false}
                                            />
                                        }
                                    >
                                        <FigureViewer
                                            src={figure.image}
                                            alt={figure.ttsDescription || (questionIndex + 1).toString()}
                                            imgClassName={`bcaModule__figure${ttsActiveId === `figure-${figure.id}` ? ' bcaModule__tts--active' : ''}`}
                                        />
                                    </ToolErrorBoundary>
                                ))}
                                <p className={`bcaModule__questionQuestion${ttsActiveId === `stem-${questions[questionIndex].id}` ? ' bcaModule__tts--active' : ''}`}>
                                    <span className='katex'>
                                        {questions[questionIndex].question}
                                    </span>
                                </p>
                                {questions[questionIndex].choices.length
                                ?   <>
                                        {questions[questionIndex].choices.map((choice, i) => (
                                            <div key={choice.id} className={`bcaModule__questionChoiceWrapper${ttsActiveId === `choice-${choice.id}` ? ' bcaModule__tts--active' : ''}`}>
                                                <button
                                                    className={answers[questionIndex].answer == choice.id
                                                        ? 'bcaModule__questionChoice--selected'
                                                        : crossOut && answers[questionIndex].crossedOut.includes(choice.id)
                                                        ? 'bcaModule__questionChoice--crossedOut'
                                                        : 'bcaModule__questionChoice'
                                                    }
                                                    onClick={() => handleSelect(choice.id)}
                                                >
                                                    <div className='bcaModule__questionChoiceOutline'></div>
                                                    <p className='bcaModule__questionChoiceLetter'>
                                                        {MULTIPLE_CHOICES[i]}
                                                    </p>
                                                    {choice.figure
                                                    ?   <img
                                                            className='bcaModule__questionChoiceFigure'
                                                            key={choice.figure}
                                                            src={choice.figure}
                                                            alt={choice.figureTtsDescription || choice.figure}
                                                            draggable={false}
                                                        />
                                                    :   <span className='bcaModule__questionChoiceTextWrapper'>
                                                            <p className='katex bcaModule__questionChoiceText'>
                                                                {choice.choice}
                                                            </p>
                                                        </span>
                                                    }
                                                </button>
                                                {crossOut &&
                                                    <button
                                                        // className={answers[questionIndex].crossedOut[choice.id]
                                                        className={answers[questionIndex].crossedOut.includes(choice.id)
                                                            ? 'bcaModule__crossOutChoice--selected'
                                                            : 'bcaModule__crossOutChoice'
                                                        }
                                                        onClick={() => crossOutChoice(choice.id)}
                                                    >
                                                        {MULTIPLE_CHOICES[i]}
                                                    </button>
                                                }
                                            </div>
                                        ))}
                                    </>
                                :   null
                                }
                                <div className='bcaModule__marginBottom'></div>
                            </div>
                        </div>
                    </div>
                </div>
            :   <div className='bcaModule__review'>
                    <h2 className='bcaModule__reviewTitle'>
                        Check your work
                    </h2>
                    <p className='bcaModule__reviewText'>
                        Click a question to go back to it. When you’re done, click <b>Submit</b>.
                        <br/>
                        Unanswered questions earn 0 points; incorrect answers lose 0.5 point.
                    </p>
                    <div className='bcaModule__reviewLegends'>
                        <h3 className='bcaModule__reviewModuleName'>{moduleName} Questions</h3>
                        <div className='bcaModule__reviewLegend'>
                            <div className='bcaModule__reviewLegendUnanswered'></div>
                            <p className='bcaModule__reviewLegendText'>
                                Unanswered
                            </p>
                        </div>
                        <div className='bcaModule__reviewLegend'>
                            <MdOutlineBookmark className='bcaModule__reviewLegendMarked' />
                            <p className='bcaModule__reviewLegendText'>
                                For review
                            </p>
                        </div>
                    </div>
                    <div className='bcaModule__reviewItems'>
                        {questions.map((question, i) => (
                            <button
                                key={question.id}
                                className={answers[i].answer
                                    ? 'bcaModule__reviewItem--answered'
                                    : 'bcaModule__reviewItem'
                                }
                                onClick={() => {
                                    goToQuestion(i);
                                    setNavigatorOpen(false);
                                }}
                            >
                                {questionIndex === i
                                    ? <u className='bcaModule__reviewItemNumber'>{i + 1}</u>
                                    : <p className='bcaModule__reviewItemNumber'>{i + 1}</p>
                                }
                                {questionIndex === i && <MdOutlineLocationOn className='bcaModule__reviewItemCurrent' />}
                                {answers[i].marked && <MdOutlineBookmark className='bcaModule__reviewItemMarked' />}
                            </button>
                        ))}
                    </div>
                </div>
            }
            <div className='bcaModule__dottedLine'></div>
            <div className='bcaModule__footer'>
                <p className='bcaModule__tester'>
                    {user.firstName} {user.lastName}
                </p>
                {questionIndex < questions.length &&
                    <button
                        className='bcaModule__navigatorToggle'
                        onClick={() => setNavigatorOpen(!navigatorOpen)}
                    >
                        Question {questionIndex + 1}/{questions.length}
                        {navigatorOpen
                            ? <MdKeyboardArrowDown className='bcaModule__navigatorToggleIcon' />
                            : <MdKeyboardArrowUp className='bcaModule__navigatorToggleIcon' />
                        }
                    </button>
                }
                <div className='bcaModule__buttons'>
                    <Button
                        className='bcaModule__button'
                        secondary
                        disabled={questionIndex === 0}
                        onClick={() => goToQuestion(questionIndex - 1)}
                    >
                        BACK
                    </Button>
                    <Button
                        className='bcaModule__button'
                        onClick={() => questionIndex >= questions.length
                            ? handleSubmit()
                            : goToQuestion(questionIndex + 1)
                        }
                    >
                        {questionIndex >= questions.length ? 'SUBMIT' : 'NEXT'}
                    </Button>
                </div>
                {navigatorOpen &&
                    <div className='bcaModule__navigator' ref={navigatorRef}>
                        <div className='bcaModule__navigatorBubble'>
                            <h2 className='bcaModule__navigatorTitle'>
                                {moduleName} Questions
                            </h2>
                            <div className='bcaModule__navigatorLegends'>
                                <div className='bcaModule__navigatorLegend'>
                                    <MdOutlineLocationOn className='bcaModule__navigatorLegendCurrent' />
                                    <p className='bcaModule__navigatorLegendText'>
                                        Current
                                    </p>
                                </div>
                                <div className='bcaModule__navigatorLegend'>
                                    <div className='bcaModule__navigatorLegendUnanswered'></div>
                                    <p className='bcaModule__navigatorLegendText'>
                                        Unanswered
                                    </p>
                                </div>
                                <div className='bcaModule__navigatorLegend'>
                                    <MdOutlineBookmark className='bcaModule__navigatorLegendMarked' />
                                    <p className='bcaModule__navigatorLegendText'>
                                        For review
                                    </p>
                                </div>
                            </div>
                            <div className='bcaModule__navigatorItems'>
                                {questions.map((question, i) => (
                                    <button
                                        key={`${question.id}nav`}
                                        className={answers[i].answer
                                            ? 'bcaModule__navigatorItem--answered'
                                            : 'bcaModule__navigatorItem'
                                        }
                                        onClick={() => {
                                            goToQuestion(i);
                                            setNavigatorOpen(false);
                                        }}
                                    >
                                        {questionIndex === i
                                            ? <u className='bcaModule__navigatorItemNumber'>{i + 1}</u>
                                            : <p className='bcaModule__navigatorItemNumber'>{i + 1}</p>
                                        }
                                        {questionIndex === i && <MdOutlineLocationOn className='bcaModule__navigatorItemCurrent' />}
                                        {answers[i].marked && <MdOutlineBookmark className='bcaModule__navigatorItemMarked' />}
                                    </button>
                                ))}
                            </div>
                            <Button
                                className='bcaModule__navigatorButton'
                                secondary
                                onClick={() => {
                                    goToQuestion(questions.length);
                                    setNavigatorOpen(false);
                                }}
                            >
                                GO TO REVIEW PAGE
                            </Button>
                        </div>
                    </div>
                }
            </div>
        </div>
    );
}

export default DigitalBCAModule;